import {
  DEFAULT_FILTER_STATE,
  MAX_DISTANCE_MILES,
  nextDistancePreset,
} from '../filters/Filters.js';
import type { WhenChoice } from './list-view.js';

/** Two weeks. The automatic step after an empty default couple-of-days view. */
export const FALLBACK_WINDOW_DAYS = 14;

export interface FallbackScope {
  radiusMiles: number;
  windowDays: number;
  when: WhenChoice;
}

/** What the fallback chain needs from a page. `resultCount` is the full match, not one slice. */
export interface FallbackPage {
  resultCount: number;
  nearestOutsideRadiusMiles?: number;
}

/**
 * The landing search: next couple of days within 5 miles.
 * Auto-fallback starts only here. Today, This weekend, and a chosen 1 mile
 * stay on the empty-state buttons from the shorter-window work.
 */
export function isDefaultLanding(scope: FallbackScope): boolean {
  return (
    scope.when === DEFAULT_FILTER_STATE.when &&
    scope.windowDays === DEFAULT_FILTER_STATE.windowDays &&
    scope.radiusMiles === DEFAULT_FILTER_STATE.radiusMiles
  );
}

/**
 * Next radius chip that can still find something after `currentMiles` came back empty.
 * A known nearest event snaps to the next chip (5 → 25). No nearest, or a nearest
 * past every chip, still tries 25 miles once: unknown locations are eligible only
 * at that metro-wide radius. Returns null at the 25 mile cap.
 */
export function nextWiderRadius(
  currentMiles: number,
  nearestOutsideMiles: number | undefined,
): number | null {
  if (currentMiles >= MAX_DISTANCE_MILES) return null;
  if (nearestOutsideMiles !== undefined) {
    const preset = nextDistancePreset(nearestOutsideMiles);
    if (preset !== null && preset > currentMiles) return preset;
  }
  return MAX_DISTANCE_MILES;
}

/**
 * Next automatic query after an empty page, or null when this page should render.
 * Chain, and only from the default landing search:
 * 1. Two weeks at the same radius.
 * 2. Two weeks at the next sensible radius, when that is still empty.
 */
export function nextAutomaticQuery(
  requested: FallbackScope,
  fetched: FallbackScope,
  page: FallbackPage,
): FallbackScope | null {
  if (page.resultCount > 0) return null;
  if (!isDefaultLanding(requested)) return null;

  const sameAsRequested =
    fetched.windowDays === requested.windowDays && fetched.radiusMiles === requested.radiusMiles;
  if (sameAsRequested) {
    return {
      radiusMiles: requested.radiusMiles,
      windowDays: FALLBACK_WINDOW_DAYS,
      when: 'two_weeks',
    };
  }

  const windowWidened =
    fetched.windowDays === FALLBACK_WINDOW_DAYS && fetched.radiusMiles === requested.radiusMiles;
  if (!windowWidened) return null;

  const radiusMiles = nextWiderRadius(fetched.radiusMiles, page.nearestOutsideRadiusMiles);
  if (radiusMiles === null) return null;
  return {
    radiusMiles,
    windowDays: FALLBACK_WINDOW_DAYS,
    when: 'two_weeks',
  };
}

export interface FallbackNotice {
  fromDays: number;
  fromMiles: number;
  toDays: number;
  toMiles: number;
}

/** Notice payload when the shown scope left the default landing search. */
export function fallbackNoticeFor(
  requested: FallbackScope,
  shown: FallbackScope,
): FallbackNotice | null {
  if (!isDefaultLanding(requested)) return null;
  if (shown.windowDays === requested.windowDays && shown.radiusMiles === requested.radiusMiles) {
    return null;
  }
  return {
    fromDays: requested.windowDays,
    fromMiles: requested.radiusMiles,
    toDays: shown.windowDays,
    toMiles: shown.radiusMiles,
  };
}

function milesPhrase(miles: number): string {
  return miles === 1 ? '1 mile' : `${miles} miles`;
}

/** Visible sentence for the wider-window banner. */
export function fallbackNoticeText(notice: FallbackNotice): string {
  const showing =
    notice.toDays === FALLBACK_WINDOW_DAYS
      ? `the next 2 weeks within ${milesPhrase(notice.toMiles)}`
      : `the next ${notice.toDays} days within ${milesPhrase(notice.toMiles)}`;
  if (notice.toMiles === notice.fromMiles) {
    return `Nothing with free food in the next couple of days. Showing ${showing}.`;
  }
  return `Nothing with free food in the next couple of days within ${milesPhrase(notice.fromMiles)}. Showing ${showing}.`;
}

/** Button that puts the landing search back on screen. */
export function fallbackReturnLabel(notice: FallbackNotice): string {
  if (notice.toMiles !== notice.fromMiles) {
    return `Show ${milesPhrase(notice.fromMiles)} and the next couple of days`;
  }
  return 'Show the next couple of days';
}

/**
 * Walk the empty-landing chain. `allowAuto` false fetches the requested scope
 * once, so "back to the narrower view" does not immediately widen again.
 * At most two extra queries (two weeks, then a wider radius).
 */
export async function resolveEmptyFallback<T extends FallbackPage>(
  requested: FallbackScope,
  allowAuto: boolean,
  fetchPage: (scope: FallbackScope) => Promise<T>,
): Promise<{ scope: FallbackScope; page: T }> {
  let scope = requested;
  let page = await fetchPage(scope);
  if (!allowAuto) return { scope, page };
  for (let step = 0; step < 2; step += 1) {
    const next = nextAutomaticQuery(requested, scope, page);
    if (!next) break;
    scope = next;
    page = await fetchPage(scope);
  }
  return { scope, page };
}
